import type { Load } from "../finds";
import type { AxialCoord } from "../hex";
import type { RngState } from "../rng";
import type { RunRules } from "./rules";

/**
 * The run's data model: the kingdoms, a hex, a unit, a King, the seats and the run itself.
 * Every rule reads and returns these shapes; nothing here computes.
 */

/**
 * The kingdoms a run may hold: ADR-0012's sixteen, thirty-two since K6 for the war's sides. The
 * duel's two keep their names, so its rules, tests and gates read as before; a run lists the seats
 * it uses in `RunState.factions`.
 */
export const FACTION_IDS = [
  "player",
  "enemy",
  "seat3",
  "seat4",
  "seat5",
  "seat6",
  "seat7",
  "seat8",
  "seat9",
  "seat10",
  "seat11",
  "seat12",
  "seat13",
  "seat14",
  "seat15",
  "seat16",
  "seat17",
  "seat18",
  "seat19",
  "seat20",
  "seat21",
  "seat22",
  "seat23",
  "seat24",
  "seat25",
  "seat26",
  "seat27",
  "seat28",
  "seat29",
  "seat30",
  "seat31",
  "seat32",
] as const;

export type FactionId = (typeof FACTION_IDS)[number];

/** A value per kingdom in the run; a seat the run does not use has none. */
export type FactionTable<Value> = Readonly<Partial<Record<FactionId, Value>>>;

export type HexOwner = "none" | FactionId;

export type RunStatus = "running" | "won" | "lost";

/** Land kinds (Increment 3.10): open plains, or rough ground that carries walls. */
export type Terrain = "open" | "rough";

/**
 * Structures. A city (Increment 3.16) is where everything comes from: only land within
 * `cityReach` of an own working city pays supply, and only a city builds units (Increment 4).
 * Both starts are cities; procgen places neutral ones behind walls; a faction founds more
 * `cityMinSpacing` apart. A well adds supply on the hex it sits on when that hex is in reach.
 * A tower (Increment 3.12) holds walls like rough ground. A farm and a hamlet (A54,
 * Increment 7.3) are improvements a faction buys on its land in reach: the farm pays supply,
 * the hamlet grows recruits; finished, they change hands with the land.
 */
export type StructureKind = "none" | "city" | "well" | "tower" | "farm" | "hamlet";

/**
 * The roster (Increment 4, ADR-0010, docs/design/UNITS-PIVOT-IDEA.md). The order is the
 * tie-break order wherever kinds are compared.
 */
export type UnitKind = "spear" | "sword" | "archer" | "rider" | "siege";

export const UNIT_KINDS: readonly UnitKind[] = ["spear", "sword", "archer", "rider", "siege"];

/**
 * A unit: the only thing that fights or takes land. One per hex. `id` is creation order and
 * the resolution order of every step. `destination` is where it walks; absent when idle.
 */
export interface Unit {
  readonly id: number;
  readonly kind: UnitKind;
  readonly owner: FactionId;
  readonly coord: AxialCoord;
  readonly hp: number;
  readonly destination?: AxialCoord;
  /** Tick of its last step (its birth tick before it moves). */
  readonly movedTick: number;
  /** Tick of its latest order (its birth tick before any); the order stands until the next move step. */
  readonly orderedTick: number;
  /** Promoted by its King (ADR-0011 slice 3): it leads a squad and holds a pip of Trust while alive. */
  readonly commander: boolean;
  /** Where the King has aimed this commander; absent until aimed. */
  readonly target?: AxialCoord;
  /** The id of the commander whose squad this unit is in; absent when it is the King's own. */
  readonly squadOf?: number;
  /** Commanders only: the muster for the banner it was dealt; absent without a muster point. */
  readonly muster?: Muster;
  /** The own city this soldier walks to, to take office as its mayor (A32); absent otherwise. */
  readonly governs?: AxialCoord;
  /**
   * Enemies whose death this unit's blow completed (ADR-0013 slice 2); 0 at birth. A veteran
   * is derived from it (`isVeteran`), never stored.
   */
  readonly kills: number;
  /**
   * The tick this unit broke (ADR-0013 slice 3): it walks home and takes no order until
   * `tactics.breakTicks` have passed or it stands on an own city. Absent while it holds.
   */
  readonly brokenTick?: number;
  /**
   * The seat whose order this unit last took (ADR-0011 slice 5): the King's, a mayor's, a
   * commander's or its own. Absent until the first order; kept after it, the way `orderedTick`
   * is, so a later seat can defer to it.
   */
  readonly orderedBy?: Role;
  /**
   * The find this soldier carries (ADR-0014 slice M2): it steps slower under it, sells it in or
   * beside a working city of its kingdom, drops it when it breaks and loses it when it dies.
   */
  readonly carry?: Load;
  /**
   * The city whose column this soldier marches in (City Command, CC1): the one that trained it,
   * or the nearest working one once that has fallen. Absent outside a city-command kingdom.
   */
  readonly home?: AxialCoord;
  /**
   * The hex the human's soldier was sent to work (CC5, `soldier.workRoute`): on its road there it
   * stops on the land its seat has not read or its King pays for, and there it works the hexes
   * within `patchRadius`. Absent otherwise, and once the work is done or ended.
   */
  readonly works?: AxialCoord;
  /**
   * VC: the tick the human's soldier bought Command; it leads its own men (`squadOf` its id)
   * while the field stands, and the field dies with the unit. Never a commander's badge: no
   * script steers it, deals it banners or counts it. Absent otherwise.
   */
  readonly leadsSince?: number;
}

export interface HexState {
  readonly coord: AxialCoord;
  readonly owner: HexOwner;
  /** Razed hexes are permanent walls: nobody owns, enters or produces on them again this run. */
  readonly razed: boolean;
  readonly terrain: Terrain;
  readonly structure: StructureKind;
  /** Walls block entry to a foreign hex until ground to zero; they regrow while no hostile unit stands beside the hex. */
  readonly walls: number;
  /** Tick a built structure starts working; 0 when complete or absent. Capture before then loses it. */
  readonly structureReadyTick: number;
  /** The unit this city is building; "none" when idle. */
  readonly unitBuild: UnitKind | "none";
  /** Tick the unit in progress is ready; 0 when none. */
  readonly unitBuildReadyTick: number;
  /** A mayor sent by the owner's King runs this city (ADR-0011 slice 2, A32); lost with the city. */
  readonly mayor: boolean;
  /** The mayor's stance (A45); absent is garrison. Read through `stanceOf`: it means nothing without a mayor. */
  readonly stance?: MayorStance;
  /** The id of the leader this lord commissioned (A46); read through `lordsLeader`. */
  readonly leader?: number;
  /**
   * City Command (CC1), a city-command kingdom's working city only: the kind its yard trains,
   * absent for Auto (the counter-pick) or "pause"; where its column goes, absent for Home; and the
   * tick its arrow was last set, Home included, from which a column waits out its muster.
   */
  readonly makes?: UnitKind | "pause";
  readonly arrow?: AxialCoord;
  readonly arrowTick?: number;
}

/**
 * What the King tells a mayor to do (A45): save trains nothing, garrison is the mayor of
 * ADR-0011 slice 2, muster holds the soldiers by the city and commissions a leader for them,
 * develop (A54) trains nothing and buys improvements on his land instead.
 */
export type MayorStance = "save" | "garrison" | "muster" | "develop";

export type PaidCommand =
  | "order"
  | "train"
  | "tower"
  | "city"
  | "improve"
  | "raze"
  | "appoint"
  | "promote"
  | "aim"
  | "stance"
  | "call"
  | "mark"
  | "tax"
  | "rush";

export type CommandKind = PaidCommand | "stop" | "dismiss" | "demote";

/**
 * The seat a command comes from (ADR-0011 slice 5, ADR-0014): the King's are checked by the
 * court and spend Will; a delegate's (a mayor's, a commander's, a soldier's own) are not: its
 * cadence is its budget. `core/command.ts` is the door every seat's command goes through.
 */
export type Role = "king" | "mayor" | "commander" | "soldier";

/** Why a command is refused by the court: the hex is out of the King's reach, or the pool is short. */
export type CommandRefusal = "reach" | "will";

export type CallKind = "capture";

/** The King's standing order to the whole kingdom (A33). Attack and defend are marks since A39. */
export interface Call {
  readonly kind: CallKind;
}

export type MarkKind = "attack" | "defend";

/** A banner the King planted on a hex (A39): foreign land to attack, own land to defend. */
export interface Mark {
  readonly kind: MarkKind;
  readonly target: AxialCoord;
  /** An attack banner's muster point (A42): own land where the squad dealt to it gathers first. */
  readonly from?: AxialCoord;
}

/** A commander's muster for the banner on `target` (A42): when it reached the point, and whether it has struck. */
export interface Muster {
  readonly target: AxialCoord;
  readonly sinceTick?: number;
  readonly done: boolean;
}

/**
 * A faction's King: a token on an own hex, not a unit (it shares its hex with one, is never
 * struck and never blocks). `capital` is the faction's starting city for the whole run.
 */
export interface King {
  readonly coord: AxialCoord;
  readonly destination?: AxialCoord;
  /** Tick of its last step. */
  readonly movedTick: number;
  readonly will: number;
  /** Times the court has fled; each costs `flightPenalty` Trust for the rest of the run. */
  readonly flights: number;
  readonly capital: AxialCoord;
  /** The standing call; absent when the kingdom has none. */
  readonly call?: Call;
  /** The banners, the most urgent first (A39); absent when none is planted. */
  readonly marks?: readonly Mark[];
  /** The tax runs until this tick and can be raised again at `taxReadyTick`; absent before the first. */
  readonly taxUntilTick?: number;
  readonly taxReadyTick?: number;
  /** The rushed city and the tick its rush ends; the next rush is ready at `rushReadyTick`. */
  readonly rush?: { readonly city: AxialCoord; readonly untilTick: number };
  readonly rushReadyTick?: number;
  /** City Command (CC1): the site he rides to found a city on, its price already paid. */
  readonly founds?: AxialCoord;
  /** City Command (CC1): after a flight he gives no courage and no presence until this tick. */
  readonly shakenUntil?: number;
}

/** The deeds of one tick (C1): what the human's soldier did for its King and what it got. */
export interface Deed {
  readonly tick: number;
  /** Hexes taken by standing and banners met in their ring, that tick. */
  readonly land: number;
  readonly banners: number;
  /** What the pool paid at once, and what it could not pay and owes. */
  readonly paid: number;
  readonly owed: number;
  readonly renown: number;
  /**
   * The deeds of company (C2), each absent while 0 so a deed of C1 keeps its bytes: hexes the
   * squad took while the soldier marched with it, loads sold in company and what the company
   * added to their pay, broken comrades steadied, kills struck together.
   */
  readonly squad?: number;
  readonly sales?: number;
  readonly bonus?: number;
  readonly steadied?: number;
  readonly kills?: number;
  /** A68: banners the squad met while the soldier marched with it, out of their ring. Absent while 0. */
  readonly squadBanners?: number;
  /**
   * V1, each absent while 0: kills by its blow (an enemy veteran's apart, in `veteransSlain`),
   * foes broken by its blow, and the valor the tick's deeds earned. `kills` stays C2's count of
   * kills struck together, so a shared kill whose last blow was its own is in both.
   */
  readonly slain?: number;
  readonly veteransSlain?: number;
  readonly routed?: number;
  readonly valor?: number;
  /**
   * VC: head money for kills by its blow, paid from the pool and owed, each absent while 0; never
   * in `paid` or `owed`, which stay the King's pay of C1 and C2.
   */
  readonly headMoneyPaid?: number;
  readonly headMoneyOwed?: number;
}

/** A seat's number (K0, ADR-0017): its place in `RunState.seats`, the order seats are resolved in. */
export type SeatId = number;

/** The roles a seat of the table holds (K0): a kingdom's King or one of its soldiers; a mayor or a commander holds none yet (C3). */
export type SeatRole = Extract<Role, "king" | "soldier">;

/**
 * A seat of the run (K0, ADR-0017 point 1, `docs/design/KINGDOM-SEATS-IDEA.md` section 5): a
 * numbered place in a kingdom with a role, held by a human or played by a script. Only the door
 * and the scripts read `held`; pay, spawn, valor and the verdict never do (`test/boundaries.test.ts`).
 * A King's seat is its first four fields. A soldier's seat (M1) plays one unit a life: the unit,
 * or, while it is dead, the tick it fell; and keeps its ledger across its deaths.
 */
export interface SeatState {
  readonly id: SeatId;
  readonly faction: FactionId;
  readonly role: SeatRole;
  /** A human gives this seat's commands; false: a script plays it. */
  readonly held: boolean;
  readonly unitId?: number;
  readonly diedTick?: number;
  /**
   * The seat's coins this run (M2; VC: every run starts with none, the account's are banked by
   * the save): what its sales and its King paid. It is the seat's, not the unit's, so a death
   * never touches it. Absent without `rules.finds`.
   */
  readonly purse?: number;
  /**
   * What the seat has earned this run by deeds for others (C1; VC: from 0, the account's is
   * banked by the save). Never spent, never lost. Absent without `rules.bounty`.
   */
  readonly renown?: number;
  /** What the King owes the seat (C1): bounties the pool could not pay. Absent without `rules.bounty`. */
  readonly owed?: number;
  /** The last tick with a deed in it (C1): what a screen and the journal say of it. Absent until the first. */
  readonly deed?: Deed;
  /**
   * The id of the commander the seat fell in with (C2). It is the seat's, not the unit's
   * `squadOf`: no script counts the human in a squad and the human follows nobody (D8). Read
   * through `enlistedWith`. Absent until the first `enlist` and once the commander is gone.
   */
  readonly enlisted?: number;
  /**
   * V1 (`soldier.valor`): the seat's valor, what its deeds at work earned less what its deaths
   * took; its title is read from it (`seatTitle`). The seat's, so it outlives a unit. Absent
   * without the block.
   */
  readonly valor?: number;
  /** V1: the most valor the seat held this run, and the tick it first reached that valor's title. */
  readonly peakValor?: number;
  readonly peakTick?: number;
  /** VC: the coins this run spent (on Command), so the run's earnings are what it holds and spent. Absent while 0. */
  readonly spent?: number;
  /** VC: head money the King owes the seat, apart from C1's `owed` and under the same cap. Absent while 0. */
  readonly headMoneyOwed?: number;
  /**
   * K1 (`soldier.seats`): the own working city the seat's next life rises at, chosen by its
   * `deploy` command; absent, the capital. A city lost since is read as the own one nearest it.
   */
  readonly deploy?: AxialCoord;
}

/**
 * The whole run as a plain value. Every field is JSON-safe so a run is fully described by
 * its serialised form; same seed plus same actions gives the same bytes on every machine.
 * `status` is from the player's point of view. `rng` is spent by procgen only.
 */
export interface RunState {
  readonly seed: number;
  readonly tick: number;
  readonly rng: RngState;
  readonly status: RunStatus;
  readonly rules: RunRules;
  readonly hexes: readonly HexState[];
  /** Creation order is the resolution order; never sort. */
  readonly units: readonly Unit[];
  /** The kingdoms in this run, the player first: the resolution order wherever several are handled in one step. */
  readonly factions: readonly FactionId[];
  /** Each faction's supply pool. */
  readonly supply: FactionTable<number>;
  /** Each faction's recruits (A53), and its growth towards the next one. Both 0 without the population block. */
  readonly population: FactionTable<number>;
  readonly growth: FactionTable<number>;
  /** The id the next unit takes. */
  readonly nextUnitId: number;
  /** Ticks in a row the enemy has been hopeless (Increment 3.15); at `surrenderTicks` it yields. */
  readonly enemyDespairTicks: number;
  /** Each faction's King; null when the rules have no court. */
  readonly kings: FactionTable<King> | null;
  /**
   * The run's seats (K0, ADR-0017): kingdom-major in `factions` order, each kingdom's King and
   * then its soldier seats (one in the player's kingdom of a soldier's run, M1; none elsewhere).
   */
  readonly seats: readonly SeatState[];
  /**
   * Keys of the hexes each kingdom's soldiers have prospected (M2; K0: the kingdom's, C2's one
   * map), in the order they were read: what its seats know of the hidden layer. Absent without
   * `rules.finds`.
   */
  readonly revealed?: FactionTable<readonly string[]>;
  /** Keys of the hexes whose find has been taken (M2), in the order they were; absent without `rules.finds`. */
  readonly spent?: readonly string[];
  /**
   * A69: what is mixed into the seed for the hidden layer, so the veins are the run's and the
   * map stays the seed's. Absent without `rules.finds` or when the run was given none: the
   * layer is then the seed's own.
   */
  readonly findsSalt?: number;
  /**
   * The seat a page shows this copy of the run to (K0), set by `viewFor` on its screen's copy
   * only, never on a state that ticks, is hashed or is saved. Absent: the screen plays the first
   * held seat (`playerSeatId`).
   */
  readonly viewer?: SeatId;
}

/**
 * The structures a faction can build. The player picks; the enemy founds first, then towers,
 * and its mayors place the improvements (A54).
 */
export type BuildKind = "tower" | "city" | "farm" | "hamlet";

export type RunEnding = "elimination" | "surrender" | "clock" | "suddenDeath";

/**
 * A record a step copies and writes in place (the move, capture and combat steps' drafts): the
 * `Written` fields writable, every other field as the record declares it, so a new field of the
 * record reaches its draft by itself.
 */
type Draft<Record, Written extends keyof Record> = Omit<Record, Written> & {
  -readonly [Key in Written]: Record[Key];
};

export type MutableHex = Draft<
  HexState,
  | "owner"
  | "structure"
  | "walls"
  | "structureReadyTick"
  | "unitBuild"
  | "unitBuildReadyTick"
  | "mayor"
  | "stance"
  | "leader"
  | "makes"
  | "arrow"
  | "arrowTick"
>;

export type MutableUnit = Draft<
  Unit,
  "coord" | "hp" | "destination" | "movedTick" | "orderedTick" | "squadOf" | "governs"
>;

/** One blow of a combat step: who strikes whom for how much, after every bonus. */
export interface Blow {
  readonly attackerId: number;
  readonly targetId: number;
  readonly blow: number;
  /** FL2: the blow was drawn heavy or glancing; absent for a steady one. */
  readonly luck?: BlowLuck;
}

/** FL2: a heavy blow lands harder, a glancing one softer. */
export type BlowLuck = "heavy" | "glancing";
